import React, { useEffect, useState } from 'react';
import notifyHB from '@/config/notifyHB';
import { toast } from '@/components/toast/toast';
import { IoChevronBackCircle } from 'react-icons/io5';
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from '@/components/ui/input-otp';
import { Button } from '@/components/ui/button';
import { Loader2Icon } from 'lucide-react';
import { loginWithOtp, LoginWithOtpResponse } from '@/use-cases/login-with-otp';
import styles from './otp-confirmation-styles.module.css';
import { login } from '@/use-cases/login';
import { RemoveNull } from '@/utils/remove-null-type';
import { getHttpErrorCode } from '@/utils/get-http-error-code';
import { getErrorReason, trackEvent } from '@/utils/analytics';

// Error codes from auth/login-confirmation that mean the OTP was rejected.
// NOT_AUTHORIZED: Cognito rejected the answer (or the session expired).
// UNEXPECTED_ERROR: inferred, not confirmed. When Cognito re-issues the
// challenge after a wrong answer, the backend hits its catch-all and returns
// this code. It is also the backend's generic 400, so narrow this set once the
// Cognito define-auth trigger behaviour is confirmed.
const INVALID_LOGIN_OTP_CODES = ['NOT_AUTHORIZED', 'UNEXPECTED_ERROR'];

export type OtpConfirmationProps = {
	sessionId: string;
	email: string;
	userId: string;
	onSuccess: (loginData: RemoveNull<LoginWithOtpResponse>) => void;
	onCancel: () => void;
};

export const OTP_TTL_SECONDS = 60 * 5;

const OtpConfirmation: React.FC<OtpConfirmationProps> = ({
	sessionId: initialSessionId,
	userId,
	email,
	onSuccess,
	onCancel,
}) => {
	const [otp, setOtp] = useState<string>('');
	const [sessionId, setSessionId] = useState(initialSessionId);
	const [loading, setLoading] = useState(false);
	const [resendSuccess, setResendSuccess] = useState(false);
	const [validSeconds, setValidSeconds] = useState(OTP_TTL_SECONDS);

	useEffect(() => {
		const intervalId = setInterval(() => {
			setValidSeconds((currentValidSeconds) =>
				currentValidSeconds > 0
					? currentValidSeconds - 1
					: currentValidSeconds,
			);
		}, 1_000);

		return () => clearInterval(intervalId);
	}, []);

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setLoading(true);
		try {
			const response = await loginWithOtp(email, sessionId, otp, userId);

			if (response.status === 200) {
				onSuccess(response as RemoveNull<LoginWithOtpResponse>);
			} else {
				toast({ mode: 'caution', description: 'Código inválido' });
				trackEvent('onboarding_error', {
					step: 'login_otp',
					reason: 'invalid_code',
				});
			}
		} catch (error: unknown) {
			const errorCode = getHttpErrorCode(error);
			if (errorCode && INVALID_LOGIN_OTP_CODES.includes(errorCode)) {
				toast({ mode: 'caution', description: 'Código inválido' });
				trackEvent('onboarding_error', {
					step: 'login_otp',
					reason: 'invalid_code',
				});
			} else {
				toast({ mode: 'destructive', description: 'Ocurrió un error inesperado. Intenta de nuevo más tarde' });
				trackEvent('onboarding_error', {
					step: 'login_otp',
					reason: `login_otp_confirm_${getErrorReason(error)}`,
				});
				notifyHB('login', 'OtpConfirmation', error);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleResendCode = async () => {
		setLoading(true);
		try {
			const response = await login(email);

			if (response.status === 200 || response.status === 201) {
				setSessionId(response.session);
				setOtp('');
				setResendSuccess(true);
				setValidSeconds(OTP_TTL_SECONDS);
				setTimeout(() => setResendSuccess(false), 3000);
			} else {
				toast({ mode: 'destructive', description: 'No se pudo reenviar el código' });
			}
		} catch (error) {
			toast({ mode: 'destructive', description: 'Ocurrió un error inesperado. Intenta de nuevo más tarde' });
			notifyHB('login', 'OtpConfirmation', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={styles['otp-confirmation']}>
			<div>
				<Button onClick={onCancel} variant={'ghost'} size="sm">
					<IoChevronBackCircle size="1.2rem" />
					Regresar
				</Button>
			</div>

			<div className={styles.title}>Confirma tu código OTP</div>
			<form onSubmit={handleSubmit} className={styles['input-group']}>
				<div className={styles['code-sent-to']}>
					Enviamos el código a <strong>{email}</strong>
				</div>
				<div className={styles.input}>
					<InputOTP
						autoFocus
						value={otp}
						onChange={setOtp}
						maxLength={6}
					>
						<InputOTPGroup>
							<InputOTPSlot index={0} />
							<InputOTPSlot index={1} />
							<InputOTPSlot index={2} />
						</InputOTPGroup>
						<InputOTPSeparator />
						<InputOTPGroup>
							<InputOTPSlot index={3} />
							<InputOTPSlot index={4} />
							<InputOTPSlot index={5} />
						</InputOTPGroup>
					</InputOTP>
				</div>
				<div className={styles.input}>
					El OTP caducará en {Math.floor(validSeconds / 60)}:
					{(validSeconds % 60).toString().padStart(2, '0')}
				</div>
			</form>
			<div className={styles['buttons']}>
				<Button
					onClick={handleSubmit}
					disabled={loading || validSeconds <= 0}
					variant="default"
					type="submit"
				>
					{loading && <Loader2Icon className="animate-spin" />}
					Verificar
				</Button>
				<div className={styles['did-not-get-code']}>
					¿Aún no recibes el OTP?
				</div>

				<Button
					onClick={handleResendCode}
					disabled={loading}
					variant="ghost"
					className={styles['resend-button']}
				>
					Reenviar código
				</Button>

				{resendSuccess && <div>Código reenviado con éxito</div>}
			</div>
		</div>
	);
};

export default OtpConfirmation;
